/**
 * User Management Component with Roles and Privileges
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Lock, Eye, EyeOff, RefreshCcw, Shield, Search, X } from 'lucide-react';
import { adminAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { ROLES, ROLE_PRIVILEGES, PRIVILEGES } from '../config/roles';
import PrivilegeEditor from './PrivilegeEditor';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import {
  flattenDivisionsTree,
  formatDivisionLabel,
  divisionIsActive,
  getAncestorDivisionIds,
  getDivisionSubtreeIds,
} from '../utils/divisionTree';

const UserManagement = () => {
  const { hasPrivilege } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [newDivisionName, setNewDivisionName] = useState('');
  const [newDivisionParentId, setNewDivisionParentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPrivileges, setShowPrivileges] = useState(null);
  const [showPrivilegeEditor, setShowPrivilegeEditor] = useState(null);

  const canCreateUser = hasPrivilege(PRIVILEGES.CREATE_USER);
  const canEditUser = hasPrivilege(PRIVILEGES.EDIT_USER);
  const canDeleteUser = hasPrivilege(PRIVILEGES.DELETE_USER);
  const canManageRoles = hasPrivilege(PRIVILEGES.MANAGE_ROLES);
  const { query: globalSearch } = useGlobalSearch();

  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = useMemo(
    () =>
      users.filter((user) => {
        const role = String(user.role || ROLES.REP).toLowerCase();
        if (roleFilter !== 'all' && role !== roleFilter) return false;

        const isActive = user.is_active !== false;
        if (statusFilter === 'active' && !isActive) return false;
        if (statusFilter === 'inactive' && isActive) return false;

        const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
        // Match visible table columns: name, email, role, status
        const searchFields = [
          user.name,
          user.email,
          role,
          roleLabel,
          isActive ? 'Active' : 'Inactive',
        ];
        if (!matchesGlobalSearch(globalSearch, searchFields)) return false;
        if (searchTerm && !matchesGlobalSearch(searchTerm, searchFields)) return false;

        return true;
      }),
    [users, globalSearch, roleFilter, statusFilter, searchTerm]
  );

  const hasActiveFilters = roleFilter !== 'all' || statusFilter !== 'all' || searchTerm.trim() !== '';
  const clearFilters = () => {
    setRoleFilter('all');
    setStatusFilter('all');
    setSearchTerm('');
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: ROLES.REP,
    is_active: true,
    division_id: '',
    reports_to_user_id: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchDivisions();
  }, []);

  // Opened from Overview → "Add Executive"
  useEffect(() => {
    if (!location.state?.openCreate) return;

    if (canCreateUser) {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: location.state.defaultRole || ROLES.REP,
        is_active: true,
      });
      setShowModal(true);
    } else {
      setError('You do not have permission to create users.');
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, canCreateUser, navigate, location.pathname]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers();
      const usersArray = Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
      // Normalize is_active to a real boolean — the API returns it as 0/1 (and
      // sometimes '0'/'1'), so checks like `is_active !== false` would otherwise
      // treat a deactivated user (0) as active after a refetch.
      const normalized = usersArray.map((u) => ({
        ...u,
        is_active: u.is_active !== 0 && u.is_active !== '0' && u.is_active !== false,
      }));
      setUsers(normalized);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
      const msg = err.response?.data?.message || err.message || 'Failed to load users';
      setError(
        msg.toLowerCase().includes('authenticate token') || msg.toLowerCase().includes('session expired')
          ? `${msg} — sign out and sign in again with Google.`
          : msg
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const res = await adminAPI.getDivisions();
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setDivisions(
        rows.map((d) => ({
          ...d,
          is_active: d.is_active !== 0 && d.is_active !== '0' && d.is_active !== false,
        }))
      );
    } catch (err) {
      // Division support may not be deployed yet; keep UI functional.
      setDivisions([]);
    }
  };

  /** Active divisions only — used in dropdowns when creating users / divisions. */
  const flatDivisions = useMemo(
    () => flattenDivisionsTree(divisions),
    [divisions]
  );

  /** All divisions including inactive — for the management list. */
  const allFlatDivisions = useMemo(
    () => flattenDivisionsTree(divisions, { includeInactive: true }),
    [divisions]
  );

  const handleCreateDivision = async () => {
    const name = String(newDivisionName || '').trim();
    if (!name) return;
    try {
      await adminAPI.createDivision({
        name,
        parent_division_id: newDivisionParentId === '' ? null : Number(newDivisionParentId),
      });
      setNewDivisionName('');
      setNewDivisionParentId('');
      await fetchDivisions();
      setSuccess('Division created');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create division';
      setError(msg);
    }
  };

  const handleSetDivisionActive = async (division, activate) => {
    const action = activate ? 'reactivate' : 'deactivate';
    const confirmMsg = activate
      ? `Reactivate "${division.name}"? It will appear in division dropdowns again.`
      : `Deactivate "${division.name}"? It will be hidden from new assignments but kept in history.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await adminAPI.updateDivision(division.id, { is_active: activate });
      await fetchDivisions();
      setSuccess(activate ? 'Division reactivated' : 'Division deactivated');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || `Failed to ${action} division`;
      setError(msg);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role || ROLES.REP,
        is_active: user.is_active !== false,
        division_id: user.division_id ?? '',
        reports_to_user_id: user.reports_to_user_id ?? '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: ROLES.REP,
        is_active: true,
        division_id: '',
        reports_to_user_id: '',
      });
    }
    setShowModal(true);
  };

  const managerOptions = useMemo(
    () => users.filter((u) => String(u.role || '').toLowerCase() === ROLES.MANAGER),
    [users]
  );
  const supervisorOptions = useMemo(
    () =>
      users.filter((u) => {
        const role = String(u.role || '').toLowerCase();
        return role === ROLES.SUPERVISOR;
      }),
    [users]
  );

  const reportsToOptions = useMemo(() => {
    const role = String(formData.role || '').toLowerCase();
    const divId = formData.division_id === '' ? null : Number(formData.division_id);

    if (role === ROLES.SUPERVISOR) {
      if (divId == null) return managerOptions;
      const allowedDivisions = new Set(getAncestorDivisionIds(divisions, divId));
      return managerOptions.filter((u) => allowedDivisions.has(Number(u.division_id)));
    }
    if (role === ROLES.REP) {
      if (divId == null) return supervisorOptions;
      const allowedDivisions = new Set(getDivisionSubtreeIds(divisions, divId));
      return supervisorOptions.filter((u) => allowedDivisions.has(Number(u.division_id)));
    }
    return [];
  }, [formData.role, formData.division_id, divisions, managerOptions, supervisorOptions]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        await adminAPI.updateUser(editingUser.id, formData);
        setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...formData } : u));
        setSuccess('User updated successfully');
      } else {
        // Create user
        const response = await adminAPI.createUser(formData);
        const newUser = response.data?.data || { id: Date.now(), ...formData };
        setUsers([...users, newUser]);
        setSuccess('User created successfully');
      }
      setTimeout(handleCloseModal, 1000);
    } catch (err) {
      console.error('Error saving user:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to save user';
      setError(msg);
      if (msg.includes('admin privileges') || msg.includes('No token')) {
        setError(`${msg} — try signing out and back in with Google.`);
      }
    }
  };

  const handleDelete = async (userId) => {
    const target = users.find((u) => u.id === userId);
    if (target && target.is_active === false) {
      setError('User is already inactive');
      return;
    }
    if (window.confirm('Deactivate this user? They will be marked inactive and will not be able to sign in.')) {
      try {
        await adminAPI.deleteUser(userId);
        setUsers(users.map((u) => (u.id === userId ? { ...u, is_active: false } : u)));
        setSuccess('User deactivated successfully');
      } catch (err) {
        console.error('Error deactivating user:', err);
        setError(err.response?.data?.message || 'Failed to deactivate user');
      }
    }
  };

  const handleSavePrivileges = async (updatedUser) => {
    try {
      const response = await adminAPI.updateUser(updatedUser.id, {
        custom_privileges: updatedUser.custom_privileges,
      });
      const saved = response.data?.data;
      const merged = saved
        ? { ...updatedUser, ...saved, custom_privileges: saved.custom_privileges ?? updatedUser.custom_privileges }
        : updatedUser;

      setUsers(users.map((u) => (u.id === updatedUser.id ? merged : u)));
      setShowPrivilegeEditor(null);
      setSuccess(`Privileges updated for ${updatedUser.name}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving privileges:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to save privileges';
      setError(msg);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      [ROLES.ADMIN]: 'bg-red-100 text-red-800',
      [ROLES.MANAGER]: 'bg-purple-100 text-purple-800',
      [ROLES.SUPERVISOR]: 'bg-blue-100 text-blue-800',
      [ROLES.REP]: 'bg-green-100 text-green-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRolePrivileges = (role) => {
    return ROLE_PRIVILEGES[role] || [];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="admin-page space-y-6">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-page-header-title">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">Manage users, roles, and permissions</p>
        </div>
        <div className="admin-page-header-actions">
          <button
            onClick={fetchUsers}
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg flex items-center hover:bg-gray-300 transition"
          >
            <RefreshCcw className="w-5 h-5 mr-2" />
            Refresh
          </button>
          {canCreateUser && (
            <button
              onClick={() => handleOpenModal()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* How to Customize Privileges Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Shield className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-2">How to Customize Privileges:</p>
            <ul className="space-y-1 ml-4 text-xs list-disc">
              <li><strong>By Role:</strong> Each user gets default privileges based on their role (Admin, Manager, Supervisor, Rep)</li>
              <li><strong>Custom Privileges:</strong> Click the <strong>"Edit"</strong> or <strong>"Custom"</strong> button in the "Customize" column to override role defaults</li>
              <li><strong>Per-Category Control:</strong> Enable/disable entire privilege categories or individual permissions</li>
              <li><strong>Reset:</strong> Use "Reset to Role Defaults" button in the editor to go back to role-based privileges</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Divisions — nested under parent division */}
      {canManageRoles && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-4">
          <div>
            <p className="font-semibold text-gray-900">Divisions</p>
            <p className="text-xs text-gray-500 mt-1">
              One organization (Settings → Company). Create top-level divisions, then child divisions under a parent (e.g. Region → City).
            </p>
          </div>
          <div className="admin-toolbar">
            <div className="admin-toolbar-field">
              <label className="block text-xs font-medium text-gray-600 mb-1">Division name</label>
              <input
                type="text"
                value={newDivisionName}
                onChange={(e) => setNewDivisionName(e.target.value)}
                placeholder="e.g. Chennai"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="admin-toolbar-field">
              <label className="block text-xs font-medium text-gray-600 mb-1">Parent division (optional)</label>
              <select
                value={newDivisionParentId}
                onChange={(e) => setNewDivisionParentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">— Top level (no parent) —</option>
                {flatDivisions.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {formatDivisionLabel(d)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleCreateDivision}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition font-medium h-[42px]"
            >
              Add division
            </button>
          </div>
          {allFlatDivisions.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <div className="admin-table-scroll">
                <table className="admin-data-table text-sm w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Division</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Parent</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {allFlatDivisions.map((d) => {
                      const active = divisionIsActive(d);
                      return (
                        <tr
                          key={d.id}
                          className={active ? 'hover:bg-gray-50' : 'bg-gray-50/80 hover:bg-gray-100'}
                        >
                          <td className="px-3 py-2 font-medium text-gray-900">
                            {formatDivisionLabel(d, { showParent: false })}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {d.parent_name || '—'}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`text-xs font-semibold ${
                                active ? 'text-green-600' : 'text-gray-500'
                              }`}
                            >
                              {active ? '✓ Active' : '✗ Inactive'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {active ? (
                              <button
                                type="button"
                                onClick={() => handleSetDivisionActive(d, false)}
                                className="inline-flex items-center text-sm text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                                title="Deactivate division"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Deactivate
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleSetDivisionActive(d, true)}
                                className="inline-flex items-center text-sm text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50"
                                title="Reactivate division"
                              >
                                Reactivate
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">✕</button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 flex justify-between items-center">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">✕</button>
        </div>
      )}

      {/* Filters: search, role, status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="admin-toolbar">
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, email, role, status…"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All roles</option>
              {Object.values(ROLES).map((role) => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-toolbar-field">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition h-[42px]"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Showing {filteredUsers.length} of {users.length} user{users.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Users — mobile cards */}
      <div className="md:hidden bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 px-4">
            {users.length === 0 ? 'No users found' : 'No users match your search.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <div key={user.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 break-words">{user.name}</div>
                    <div className="text-sm text-gray-600 break-all">{user.email}</div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${getRoleColor(user.role || ROLES.REP)}`}>
                    {(user.role || ROLES.REP).charAt(0).toUpperCase() + (user.role || ROLES.REP).slice(1)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`font-medium ${user.is_active !== false ? 'text-green-600' : 'text-gray-500'}`}>
                    {user.is_active !== false ? '✓ Active' : '✗ Inactive'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowPrivileges(showPrivileges === user.id ? null : user.id)}
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center"
                  >
                    {showPrivileges === user.id ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Hide privileges
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        View privileges ({getRolePrivileges(user.role || ROLES.REP).length})
                        {user.custom_privileges && <span className="ml-1 text-orange-500">*</span>}
                      </>
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {canManageRoles && (
                    <button
                      type="button"
                      onClick={() => setShowPrivilegeEditor(user)}
                      className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800 px-3 py-1.5 rounded-lg hover:bg-purple-50"
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      {user.custom_privileges ? 'Custom privileges' : 'Edit privileges'}
                    </button>
                  )}
                  {canEditUser && (
                    <button
                      type="button"
                      onClick={() => handleOpenModal(user)}
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                  )}
                  {canDeleteUser && user.is_active !== false && (
                    <button
                      type="button"
                      onClick={() => handleDelete(user.id)}
                      className="inline-flex items-center text-sm text-red-600 hover:text-red-800 px-3 py-1.5 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Deactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Users Table — desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-md admin-table-shell">
        <div className="admin-table-scroll">
        <table className="admin-data-table-xl">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Privileges</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Customize</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-900">{user.name}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRoleColor(user.role || ROLES.REP)}`}>
                    {(user.role || ROLES.REP).charAt(0).toUpperCase() + (user.role || ROLES.REP).slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-sm font-medium ${user.is_active !== false ? 'text-green-600' : 'text-gray-500'}`}>
                    {user.is_active !== false ? '✓ Active' : '✗ Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => setShowPrivileges(showPrivileges === user.id ? null : user.id)}
                    className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                  >
                    {showPrivileges === user.id ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        View ({getRolePrivileges(user.role || ROLES.REP).length})
                        {user.custom_privileges && <span className="ml-1 text-orange-500">*</span>}
                      </>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4">
                  {canManageRoles && (
                    <button
                      onClick={() => setShowPrivilegeEditor(user)}
                      className="text-purple-600 hover:text-purple-800 flex items-center text-sm p-2 hover:bg-purple-50 rounded-lg transition"
                      title="Customize privileges for this user"
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      {user.custom_privileges ? 'Custom' : 'Edit'}
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  {canEditUser && (
                    <button
                      onClick={() => handleOpenModal(user)}
                      className="text-blue-600 hover:text-blue-800 p-2 inline-block"
                      title="Edit user"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {canDeleteUser && user.is_active !== false && (
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="text-red-600 hover:text-red-800 p-2 inline-block"
                      title="Deactivate user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {users.length === 0 ? 'No users found' : 'No users match your search.'}
          </div>
        )}
      </div>

      {/* Privileges Details */}
      {showPrivileges && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <Lock className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-bold text-gray-900">
              Privileges for {users.find(u => u.id === showPrivileges)?.name}
            </h3>
            {users.find(u => u.id === showPrivileges)?.custom_privileges && (
              <span className="ml-2 px-3 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
                CUSTOM OVERRIDE
              </span>
            )}
          </div>
          
          {users.find(u => u.id === showPrivileges)?.custom_privileges && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-sm text-orange-800">
              ⚠️ This user has custom privileges that override the {users.find(u => u.id === showPrivileges)?.role} role defaults.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {getRolePrivileges(users.find(u => u.id === showPrivileges)?.role || ROLES.REP).map(privilege => {
              const user = users.find(u => u.id === showPrivileges);
              const isCustom = user?.custom_privileges && user.custom_privileges.includes(privilege);
              const hasPriv = user?.custom_privileges ? isCustom : true;
              
              return (
                <div
                  key={privilege}
                  className={`rounded-lg p-3 border ${
                    hasPriv
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200 opacity-50'
                  }`}
                >
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={hasPriv}
                      disabled
                      className="w-4 h-4 rounded cursor-not-allowed"
                    />
                    <p className="text-sm font-medium ml-2">
                      {privilege.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md sm:max-w-lg my-6">
            <div className="p-4 sm:p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>

                {canManageRoles && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      {Object.values(ROLES).map(role => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)} ({getRolePrivileges(role).length} privileges)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Division + Reporting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                  <select
                    value={formData.division_id}
                    onChange={(e) => setFormData({ ...formData, division_id: e.target.value, reports_to_user_id: '' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    <option value="">-- Select Division --</option>
                    {flatDivisions.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {formatDivisionLabel(d)}
                      </option>
                    ))}
                  </select>
                </div>

                {String(formData.role || '').toLowerCase() === ROLES.SUPERVISOR && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reports To (Manager)</label>
                    <select
                      value={formData.reports_to_user_id}
                      onChange={(e) => setFormData({ ...formData, reports_to_user_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="">-- None --</option>
                      {reportsToOptions.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {String(formData.role || '').toLowerCase() === ROLES.REP && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reports To (Supervisor)</label>
                    <select
                      value={formData.reports_to_user_id}
                      onChange={(e) => setFormData({ ...formData, reports_to_user_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="">-- None --</option>
                      {reportsToOptions.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 mr-2 rounded"
                    />
                    Active User
                  </label>
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Privilege Editor Modal */}
      {showPrivilegeEditor && (
        <PrivilegeEditor
          user={showPrivilegeEditor}
          onClose={() => setShowPrivilegeEditor(null)}
          onSave={handleSavePrivileges}
        />
      )}
    </div>
  );
};

export default UserManagement;
